import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ZodError } from 'zod';
import { authApi, ApiError } from '../api/client';
import { signupSchema } from '../schemas/validation';
import { cn } from '../utils/cn';
import { normalizeEmail } from '../utils/email';
import { trackPixelEvent } from './FacebookPixelTracker';
import { SIGNUP_HONEYPOT_FIELD, isSignupHoneypotFilled } from '../utils/signupAntiBot';
import { ONBOARDING_PATH } from '../utils/onboarding';
import { getSafeRedirectPath } from '../utils/safeRedirect';

/** Minimum time on page before signup can be submitted (bot friction). */
export const SIGNUP_MIN_SUBMIT_DELAY_MS = 1000;

interface SignupFormProps {
  onSuccess?: () => void;
  showHeader?: boolean;
  className?: string;
  /** When set, blocks submit until this many ms have passed since mount. */
  minSubmitDelayMs?: number;
}

export const SignupForm: React.FC<SignupFormProps> = ({
  onSuccess,
  showHeader = true,
  className = '',
  minSubmitDelayMs = SIGNUP_MIN_SUBMIT_DELAY_MS,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = getSafeRedirectPath(searchParams.get('redirect'));
  const mountedAtRef = useRef(Date.now());
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [honeypot, setHoneypot] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [submitUnlocked, setSubmitUnlocked] = useState(minSubmitDelayMs <= 0);

  useEffect(() => {
    if (minSubmitDelayMs <= 0) {
      setSubmitUnlocked(true);
      return;
    }

    // Unlock only after the submit guard below would allow the request.
    // A timer can fire on the same millisecond the guard still rejects.
    setSubmitUnlocked(false);
    let timer = 0;
    const arm = () => {
      const elapsed = Date.now() - mountedAtRef.current;
      if (elapsed > minSubmitDelayMs) {
        setSubmitUnlocked(true);
        return;
      }
      timer = window.setTimeout(arm, minSubmitDelayMs - elapsed + 1);
    };
    arm();
    return () => window.clearTimeout(timer);
  }, [minSubmitDelayMs]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (minSubmitDelayMs > 0 && Date.now() - mountedAtRef.current <= minSubmitDelayMs) {
      setErrors({ api: 'Please wait a moment, then try again.' });
      return;
    }

    if (isSignupHoneypotFilled(honeypot)) {
      setErrors({
        api: 'A hidden field was filled. Refresh the page and try again.',
      });
      return;
    }

    setErrors({});
    setIsLoading(true);

    try {
      // Validate form data
      const validatedData = signupSchema.parse(formData);

      // Call API (canonical email; server also normalizes)
      const response = await authApi.signup({
        ...validatedData,
        email: normalizeEmail(validatedData.email),
      });

      // Store user info
      localStorage.setItem('user', JSON.stringify(response.user));

      trackPixelEvent('CompleteRegistration');

      const u = response.user as { emailVerified?: boolean; isAdmin?: boolean };
      const postAuthPath = redirectParam || ONBOARDING_PATH;
      if (!u.isAdmin && u.emailVerified === false) {
        try {
          // After they confirm email, continue to redirect (if safe) or new-user onboarding.
          sessionStorage.setItem('afterEmailVerify', postAuthPath);
        } catch {
          /* ignore (private mode, quota) */
        }
        navigate('/verify-email', {
          replace: true,
          state: { verificationEmailSent: response.verificationEmailSent !== false },
        });
        return;
      }

      // Call onSuccess callback or navigate to pricing page for plan selection
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(postAuthPath);
      }
    } catch (error) {
      if (error instanceof ApiError) {
        setErrors({ api: error.message });
      } else if (error instanceof ZodError) {
        // Client-side Zod validation errors - extract user-friendly message
        const firstError = error.errors[0];
        if (firstError) {
          const field = firstError.path[0] as string;
          if (field) {
            // Set field-specific error (e.g., password, email, name)
            setErrors({ [field]: firstError.message });
          } else {
            setErrors({ api: firstError.message });
          }
        } else {
          setErrors({ api: 'Invalid input data' });
        }
      } else if (error instanceof Error) {
        setErrors({ api: error.message });
      } else {
        setErrors({ api: 'An unexpected error occurred' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className={`max-w-md w-full space-y-8 ${className}`}>
      {showHeader && (
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link
              to={redirectParam ? `/login?redirect=${encodeURIComponent(redirectParam)}` : '/login'}
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              sign in to your existing account
            </Link>
          </p>
        </div>
      )}

      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
          <label htmlFor={SIGNUP_HONEYPOT_FIELD}>Company website</label>
          <input
            id={SIGNUP_HONEYPOT_FIELD}
            name={SIGNUP_HONEYPOT_FIELD}
            type="text"
            tabIndex={-1}
            autoComplete="off"
            data-1p-ignore="true"
            data-lpignore="true"
            data-bwignore="true"
            value={honeypot}
            onChange={e => setHoneypot(e.target.value)}
          />
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Full name
            </label>
            <input
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              required
              value={formData.name}
              onChange={handleChange}
              className={cn(
                'input-field mt-1',
                errors.name && 'border-red-300 focus:border-red-500 focus:ring-red-500'
              )}
              placeholder="Enter your full name"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={formData.email}
              onChange={handleChange}
              className={cn(
                'input-field mt-1',
                errors.email && 'border-red-300 focus:border-red-500 focus:ring-red-500'
              )}
              placeholder="Enter your email"
            />
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              required
              value={formData.password}
              onChange={handleChange}
              className={cn(
                'input-field mt-1',
                errors.password && 'border-red-300 focus:border-red-500 focus:ring-red-500'
              )}
              placeholder="Enter your password (min 8 characters)"
            />
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            <p className="mt-1 text-sm text-gray-500">Must be at least 8 characters long</p>
          </div>
        </div>

        {errors.api && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-600">{errors.api}</p>
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={isLoading || !submitUnlocked}
            className={cn(
              'btn-primary w-full',
              (isLoading || !submitUnlocked) && 'opacity-50 cursor-not-allowed'
            )}
          >
            {isLoading ? 'Creating account...' : 'Select a Plan'}
          </button>
        </div>
      </form>
    </div>
  );
};
